// Phase 5.0 — derive a single "health color" for a workspace
// from the fields /system/status returns.
//
// Four colors map to the operator's mental model:
//
//   green  — fully healthy: vault active, polling recent, no
//            recent slack_error, no per-team Drive trouble.
//   yellow — concerning but not broken: stale poll, in-progress
//            backfill, or this workspace has rows in the Drive
//            queue's retrying bucket.
//   red    — broken: vault needs_reauth/disabled, OR any dead
//            rows exist anywhere in the Drive queue.
//   gray   — state unknown: vault row exists but never validated
//            (fresh login, daemon hasn't touched yet).
//
// Why this lives in lib/ and not the component: the same logic
// drives both the workspace-rail dot AND the /status page row
// color, and is independently unit-tested. The component just
// renders whatever color the helper returns.
//
// "Dead row" detection note: /system/status's drive_queue is
// SYSTEM-WIDE (no per-team breakdown). A dead row anywhere in
// the pipeline colors EVERY workspace red, not just the one the
// row belongs to. That's intentional — at the live workload
// (two workspaces, single operator) a dead row is a global
// system event the operator needs to see no matter which
// workspace they happen to be looking at. A future pass could
// query per-team queue stats and narrow this.

import type {
  DriveQueueDepth,
  SystemStatusWorkspace,
} from "./api";

export type HealthColor = "green" | "yellow" | "red" | "gray";

/** A polled-within-this-many-minutes counts as "recent enough" for
 *  green. 30 minutes is loose enough that the default 15-minute
 *  poll cadence has 2x margin (one missed cycle is fine), tight
 *  enough that the operator notices within an hour if polling has
 *  silently wedged. */
export const STALE_POLL_MINUTES = 30;

export interface WorkspaceHealthInputs {
  workspace: SystemStatusWorkspace;
  /** Whole-system Drive queue depth. Pass null when drive_enabled
   *  is false — the helper treats Drive trouble as a non-signal in
   *  that case (legacy-single-laptop deployments don't have a
   *  queue to be jammed). */
  driveQueue: DriveQueueDepth | null;
  /** Optional override for tests; defaults to Date.now(). */
  now?: number;
}

export function workspaceHealthColor(inputs: WorkspaceHealthInputs): HealthColor {
  const { workspace: w, driveQueue } = inputs;
  const now = inputs.now ?? Date.now();

  // Red: vault status is broken, OR there are dead Drive uploads
  // anywhere. The vault check is per-workspace; the dead-row
  // check is system-wide (see file header).
  if (w.vault_status === "needs_reauth" || w.vault_status === "disabled") {
    return "red";
  }
  if (driveQueue && driveQueue.dead > 0) {
    return "red";
  }

  // Gray: the daemon has never validated this workspace. Distinct
  // from "validated and stale" — gray says "we don't know yet,"
  // green/yellow say "we know and it's [healthy/concerning]."
  if (w.last_validated_at === null) {
    return "gray";
  }

  // Yellow: in-progress backfill (workspace has been polled at
  // least once but backfill not yet complete), OR poll is stale,
  // OR there are Drive uploads retrying anywhere in the system.
  const polledMsAgo = w.last_polled_at
    ? now - Date.parse(w.last_polled_at)
    : null;
  const pollIsStale =
    polledMsAgo !== null && polledMsAgo > STALE_POLL_MINUTES * 60_000;

  if (pollIsStale) {
    return "yellow";
  }
  if (!w.backfill_complete && w.last_polled_at !== null) {
    return "yellow";
  }
  if (driveQueue && driveQueue.retrying > 0) {
    return "yellow";
  }

  // Green: everything is current and clean. (last_polled_at null
  // but vault active is a transitional state — fresh validate, no
  // poll yet — which we render green because the daemon's first
  // poll tick is imminent and the operator doesn't need to be
  // alarmed mid-onboarding.)
  return "green";
}

/** The short badge text for a workspace's health.
 *
 *  Why this exists: "yellow" covers three very different situations —
 *  a stale poll (the sync daemon has stopped), an in-progress backfill,
 *  and a retrying Drive upload. Rendering all three as "Syncing" is a
 *  lie in the first case, and an actively harmful one: on 2026-07-09
 *  the sync pod wedged and ARKIV 360 sat there reading "Syncing" for
 *  17 hours while nothing was syncing at all. A dead sync must say so.
 *
 *  Keep in step with workspaceHealthColor — same order of checks. */
export function workspaceHealthLabel(inputs: WorkspaceHealthInputs): string {
  const { workspace: w, driveQueue } = inputs;
  const now = inputs.now ?? Date.now();

  if (w.vault_status === "needs_reauth" || w.vault_status === "disabled") {
    return "Needs attention";
  }
  if (driveQueue && driveQueue.dead > 0) {
    return "Needs attention";
  }
  if (w.last_validated_at === null) {
    return "Pending";
  }

  const polledMsAgo = w.last_polled_at ? now - Date.parse(w.last_polled_at) : null;
  if (polledMsAgo !== null && polledMsAgo > STALE_POLL_MINUTES * 60_000) {
    // The sync daemon has stopped touching this workspace. NOT "Syncing".
    return "Sync stale";
  }
  if (!w.backfill_complete && w.last_polled_at !== null) {
    return "Backfilling";
  }
  if (driveQueue && driveQueue.retrying > 0) {
    return "Retrying uploads";
  }
  return "Active";
}

/** Human-readable explanation of WHY a workspace is the color it
 *  is. Drives the badge's tooltip on hover + the explanatory text
 *  next to the per-row dot on /status. */
export function workspaceHealthReason(inputs: WorkspaceHealthInputs): string {
  const { workspace: w, driveQueue } = inputs;
  const now = inputs.now ?? Date.now();

  if (w.vault_status === "needs_reauth") {
    const err = w.last_slack_error?.type;
    return err
      ? `needs re-auth — ${err}`
      : "needs re-auth";
  }
  if (w.vault_status === "disabled") {
    return "disabled — account inactive";
  }
  if (driveQueue && driveQueue.dead > 0) {
    return `Drive queue has ${driveQueue.dead} dead row${driveQueue.dead === 1 ? "" : "s"}`;
  }
  if (w.last_validated_at === null) {
    return "unknown — never validated";
  }
  const polledMsAgo = w.last_polled_at ? now - Date.parse(w.last_polled_at) : null;
  if (polledMsAgo !== null && polledMsAgo > STALE_POLL_MINUTES * 60_000) {
    const minutes = Math.floor(polledMsAgo / 60_000);
    return `sync stale — last synced ${minutes}m ago`;
  }
  if (!w.backfill_complete && w.last_polled_at !== null) {
    return "backfill in progress";
  }
  if (driveQueue && driveQueue.retrying > 0) {
    return `Drive queue has ${driveQueue.retrying} retrying`;
  }
  return "healthy";
}
